import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { salesChannels } from "../../../../common/consts/SalesChannels.js";
import {
  checkIsOfferAvaliable,
  getOfferIdByExternalId,
} from "../../../../common/services/externalService.js";
import langs from "../../../../common/services/languageService.js";
import { useCountPendingRequests } from "../../../../hooks/useCountPendingRequests.js";
import TradeInResult from "../../components/trade-in-result/trade-in-result.jsx";
import "../../styles/trade-in-register.scss";
import TradeInLayout from "../layout.jsx";
import TradeInDevice from "./components/trade-in-device/trade-in-device.jsx";
import TradeInPhone from "./components/trade-in-phone/trade-in-phone.jsx";

const { labels } = langs;

function TradeInRegister() {
  const { isLoading, startRequest, finishRequest } = useCountPendingRequests();
  const [buttonLabel, setButtonLabel] = useState();
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [buttonHidden, setButtonHidden] = useState(false);
  const [submitFn, setSubmitFn] = useState(null);
  const [captchaResetHandle, setCaptchaResetHandler] = useState(null);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [transactionId, setTransactionId] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [offerId, setOfferId] = useState(null);
  const params = useParams();
  const sourceAppType = params.sourceAppType ?? null;
  const isLightApp = sourceAppType == null;
  const navigate = useNavigate();

  useEffect(() => {
    if (isLightApp) {
        const urlOfferId = params.offerId;

        setOfferId(urlOfferId);

        startRequest();
        checkIsOfferAvaliable(+urlOfferId, salesChannels.B2B)
          .catch((err) => {
            console.error(err);
            navigate("/error");
          })
          .then((response) => {
            if (!response.data) navigate("/no-offer");
          })
          .finally(() => {
            finishRequest();
          });
    }
    else {
        startRequest();
        getOfferIdByExternalId(params.externalId, sourceAppType)
          .catch((err) => {
            console.error(err);
            navigate("/error");
          })
          .then((response) => {
            if (!response.data) {
              navigate("/no-offer");
            } else {
              setOfferId(response.data);
              window.history.replaceState(null, "Żabka", "/");
            }
          })
          .finally(() => {
            finishRequest();
          });
    }
  }, [
    isLightApp,
    sourceAppType,
    finishRequest,
    startRequest,
    params.offerId,
    params.externalId,
    navigate,
  ]);

  const handleSuccessfulSubmit = useCallback((result) => {
    let formIndex = currentFormIndex;

    if (formIndex === 0) {
      setPhoneNumber(result.phoneNumber);
      setTransactionId(result.transactionId);
    }

    if (formIndex < 3) formIndex++;

    if (formIndex === 2) {
      setButtonHidden(true);
    }

    setCurrentFormIndex(formIndex);
  }, [currentFormIndex]);

  const handleButtonClick = useCallback(() => {
    if (!submitFn) return;
    submitFn();
  }, [submitFn]);

  const formSwitch = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          <TradeInPhone
            sourceAppType={sourceAppType}
            requestProps={{ startRequest, finishRequest }}
            formSubmitProps={{
              setSubmitFn,
              handleSuccessfulSubmit,
              handleSubmitError: captchaResetHandle,
              setButtonLabel,
              setButtonEnabled,
            }}
            offerId={offerId}
          ></TradeInPhone>
        );

      case 1:
        return (
          <TradeInDevice
            sourceAppType={sourceAppType}
            requestProps={{ startRequest, finishRequest }}
            formSubmitProps={{
              setSubmitFn,
              handleSuccessfulSubmit,
              handleSubmitError: captchaResetHandle,
              setButtonLabel,
              setButtonEnabled,
            }}
            transactionId={transactionId}
            phoneNumber={phoneNumber}
            offerId={offerId}
          ></TradeInDevice>
        );

      case 2:
        return (
          <TradeInResult
            message={
              labels.ThanksForUsingOffer() + " " + labels.TradeInSuccessfull()
            }
            formSubmitProps={{
              setSubmitFn,
              handleSuccessfulSubmit,
              setButtonLabel,
            }}
          />
        );

      default:
        throw new Error("Step index out of bounds");
    }
  };

  return (
    <TradeInLayout
      buttonEnabled={buttonEnabled}
      buttonLabel={buttonLabel}
      handleButtonClick={handleButtonClick}
      isLoading={isLoading}
      showReCaptcha={currentFormIndex === 0}
      buttonHidden={buttonHidden}
      setCaptchaResetHandler={setCaptchaResetHandler}
    >
      {formSwitch(currentFormIndex)}
    </TradeInLayout>
  );
}

export default TradeInRegister;
